import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { acceptInvitation } from '../account/sharing';
import { translate, useT } from '../i18n/i18n';

/**
 * Where an invitation link lands.
 *
 * <p>THE LINK IS KEPT WHILE SOMEBODY SIGNS IN. Whoever follows it very often has no session
 * yet, and sending them to the sign-in page having thrown the invitation away would leave them
 * back here with nothing to accept and no idea what went wrong.
 */
export default function InvitedPage() {
  const [search] = useSearchParams();
  const token = search.get('invite');
  const { token: session, ready } = useAuth();
  const nav = useNavigate();
  const t = useT();

  const [state, setState] = useState<'idle' | 'working' | 'done'>('idle');
  const [error, setError] = useState<string | null>(null);

  const accept = useCallback(async () => {
    if (!token) return;
    setState('working');
    setError(null);
    try {
      await acceptInvitation(token);
      setState('done');
    } catch (e) {
      const msg = e instanceof Error ? e.message : '';
      setError(msg.includes('different email address')
        ? translate('This invitation was sent to a different email address. Sign out, then sign in or create an account with the address it was sent to.')
        : msg || translate('That invitation could not be used.'));
      setState('idle');
    }
  }, [token]);

  useEffect(() => {
    if (ready && session && token && state === 'idle' && !error) void accept();
  }, [ready, session, token, state, error, accept]);

  if (!token) {
    return (
      <section style={{ maxWidth: 560, margin: '0 auto' }}>
        <div className="card">
          <h2>{t('That link is incomplete')}</h2>
          <p className="field-hint">
            {t('Ask whoever invited you to send it again — the part that identifies the invitation is missing.')}
          </p>
          <div className="toolbar"><Link className="btn" to="/">{t('Go to the app')}</Link></div>
        </div>
      </section>
    );
  }

  if (!ready) return <p className="muted">{t('Loading…')}</p>;

  if (!session) {
    return (
      <section style={{ maxWidth: 560, margin: '0 auto' }}>
        <div className="card">
          <h2>{t('You have been invited')}</h2>
          <p>
            {t('Sign in, or create an account, and you will be brought straight back here to accept.')}
          </p>
          <div className="toolbar">
            <Link className="btn" to="/login" state={{ from: `/invited?invite=${encodeURIComponent(token)}` }}>
              {t('Sign in to accept')}
            </Link>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section style={{ maxWidth: 560, margin: '0 auto' }}>
      <div className="card">
        <h2>{state === 'done' ? t('You are in') : t('You have been invited')}</h2>
        {error && <div className="error" role="alert">{error}</div>}
        {state === 'done' ? (
          <>
            <p>
              {t('You can now run programs for this learner and see how they are getting on. You cannot change anything about the subscription, and you can leave at any time from Settings.')}
            </p>
            <div className="toolbar">
              <button className="btn" onClick={() => nav('/', { replace: true })}>
                {t('Start')}
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="field-hint">
              {t("Accepting lets you work with this learner. It does not give you access to anybody's payment details.")}
            </p>
            <div className="toolbar">
              <button className="btn" onClick={() => void accept()} disabled={state === 'working'}>
                {state === 'working' ? t('Accepting…') : t('Accept the invitation')}
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
